import { useRentware } from "../lib/rentware";
import { Card } from "./ui/card";
import { ExternalLink, AlertCircle } from "lucide-react";

/**
 * Renders a Rentware widget snippet (HTML provided per-boat by admin).
 *
 * Why dangerouslySetInnerHTML?
 *   Rentware ships custom elements (e.g. `<rtr-order>`, calendar widgets) that
 *   are registered globally once the cdn script loads. The admin pastes the
 *   exact snippet generated by Rentware's backend (already scoped to the right
 *   article/order). React would otherwise treat the unknown element as a stub.
 *
 * Trust model: only admins can set `html`; non-admins only render. No public
 * input is ever inlined here.
 */
export default function RentwareEmbed({ html, fallback }) {
  const { ready, configured } = useRentware();

  if (!configured) {
    return (
      <Card className="p-5 border-dashed border-[#BD984D]/40 bg-[#BD984D]/5"
            data-testid="rentware-not-configured">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#A57F39] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#0C1824]">
            <div className="font-semibold">Rentware ikke konfigurert</div>
            <p className="text-[#2B3F4D] mt-1">
              Admin må legge inn Rentware-token under <span className="font-mono">/admin/settings</span> →
              fanen <strong>Rentware</strong>, og lime inn widget-snippet for hver båt.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (!html?.trim()) {
    return (
      <Card className="p-5 border-dashed border-[#BD984D]/40 bg-white"
            data-testid="rentware-no-snippet">
        <div className="flex items-start gap-3">
          <ExternalLink className="w-5 h-5 text-[#1DA3B3] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#0C1824]">
            <div className="font-semibold">Ingen Rentware-widget for denne båten</div>
            <p className="text-[#2B3F4D] mt-1">
              {fallback ||
                "Lim inn widget-snippet fra Rentware-dashbordet under Innstillinger for å vise booking-kalenderen her."}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (!ready) {
    return (
      <Card className="p-5" data-testid="rentware-loading">
        <p className="text-sm text-[#2B3F4D]">Laster Rentware-widget…</p>
      </Card>
    );
  }

  // The script registers custom elements globally so the inner HTML hydrates
  // as soon as it lands in the DOM.
  return (
    <div
      className="rentware-embed bg-white border border-[#BD984D]/25 rounded-xl p-4"
      data-testid="rentware-embed"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
